/* eslint-disable @nrwl/nx/enforce-module-boundaries */
/* eslint-disable @angular-eslint/component-selector */
import { NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  Host,
  HostBinding,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';

import { GsapAnimationService, GsapLandingEntranceVariant } from '@usheru-hff/core/gsap';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MovieSiteLandingVideoConfiguration } from '@usheru-hff/sdk/movie-site';
import { getChannelMovieProp, Movie } from '@usheru-hff/shared/domain';
import { VideoComponent } from '@usheru-hff/shared/ui-video';

import { MovieSiteMenuLandingComponent } from '../landing-menu/movie-site-landing-menu.component';
import { MovieSiteLogoImageComponent } from '../logo-image/movie-site-logo-image.component';
import { MovieSiteConfigurationService } from '../movie-site-configuration.service';
import { NavAction } from '../nav-action/movie-site-nav-action.model';

const GSAP_LANDING_ENTRANCE_VARIANTS = new Set<string>(['scale-blur', 'rise', 'clip-reveal']);

@Component({
  selector: 'movie-site-landing',
  standalone: true,
  imports: [
    NgClass,
    NgOptimizedImage,
    NgTemplateOutlet,
    VideoComponent,
    MovieSiteMenuLandingComponent,
    MovieSiteLogoImageComponent
  ],
  templateUrl: './movie-site-landing.component.html',
  styleUrls: ['./movie-site-landing.component.scss']
})
export class MovieSiteLandingComponent {
  readonly configService = inject(MovieSiteConfigurationService);
  private readonly channelService = inject(ChannelService);
  private readonly gsapAnimationService = inject(GsapAnimationService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly gsapEntranceStarted = signal(false);

  @HostBinding('id') id = 'landing';

  @HostBinding('class.cover-screen')
  get modeCoverClass() {
    const config = this.config();
    return config.mode === 'cover-screen';
  }

  @HostBinding('class.cover-screen-sm')
  get modeCoverSmClass() {
    const config = this.config();
    return config.modeMobile == null ? config.mode === 'cover-screen' : config.modeMobile === 'cover-screen';
  }

  @HostBinding('class.contain')
  get modeContainClass() {
    const config = this.config();
    return config.mode !== 'cover-screen';
  }

  @HostBinding('class.contain-sm')
  get modeContainSmClass() {
    const config = this.config();
    return config.modeMobile == null ? config.mode === 'contain' : config.modeMobile === 'contain';
  }

  @HostBinding('class.gradient')
  get gradientClass() {
    const config = this.config();
    return config.gradientBottom ?? false;
  }

  @HostBinding('attr.data-gradient-mobile')
  get gradientMobileAttr() {
    const config = this.config();
    return config.gradientBottomMobile ?? false;
  }

  @HostBinding('class.parallax-effect')
  get parallaxEffectClass() {
    const config = this.config();
    return config.parallaxEffect ?? false;
  }

  @HostBinding('attr.data-media-type')
  get mediaTypeAttr() {
    const config = this.config();
    return config.display || null;
  }

  @HostBinding('attr.data-logo-horizontal-position')
  get logoHorizontalPosition() {
    const config = this.config();
    const mainPosition = config.logoPosition?.mainPosition;
    return mainPosition ? mainPosition[0] || null : null;
  }

  @HostBinding('attr.data-logo-vertical-position')
  get logoVerticalPosition() {
    const config = this.config();
    const mainPosition = config.logoPosition?.mainPosition;
    return mainPosition ? mainPosition[1] || null : null;
  }

  @HostBinding('attr.data-release-message-horizontal-position')
  get releaseMessageHorizontalPosition() {
    const config = this.config();
    return config.releaseMessagePosition?.horizontal || null;
  }

  @HostBinding('attr.data-hide-logo')
  get hideLogo() {
    const config = this.config();
    return config.hideLogo ?? false;
  }

  @HostBinding('class.gsap-landing-entrance-pending')
  get gsapLandingEntrancePendingClass() {
    return this.landingEntrancePending();
  }

  @HostBinding('style.--movie-site-landing-aspect-ratio')
  get aspectRatio() {
    const config = this.config();
    return config.imageAspectRatio || null;
  }

  @HostBinding('style.--movie-site-landing-object-fit')
  get objectFit() {
    const config = this.config();
    return config.mode === 'cover-screen' && config.mediaFit !== undefined ? config.mediaFit : null;
  }

  @HostBinding('style.--movie-site-landing-object-position')
  get objectPosition() {
    const config = this.config();
    return config.mode === 'cover-screen' && config.mediaPosition !== undefined ? config.mediaPosition : null;
  }

  @HostBinding('style.--movie-site-landing-logo-position-offset-X')
  get logoOffsetX() {
    const config = this.config();
    const offset = config.logoPosition?.offset;
    return offset ? `${offset[0]}${offset[1]}` || null : null;
  }

  @HostBinding('style.--movie-site-landing-logo-position-offset-Y')
  get logoOffsetY() {
    const config = this.config();
    const offset = config.logoPosition?.offset;
    return offset ? `${offset[2]}${offset[3]}` || null : null;
  }

  @HostBinding('style.--movie-site-landing-logo-max-width')
  get logoMaxWidth() {
    const config = this.config();
    return config.logo?.maxWidth || null;
  }

  @HostBinding('style.--movie-site-landing-logo-mobile-max-width')
  get logoMobileMaxWidth() {
    const config = this.config();
    return config.logo?.mobileMaxWidth || null;
  }

  @HostBinding('style.--movie-site-landing-release-message-max-width')
  get releaseMessageMaxWidth() {
    const config = this.config();
    return config.releaseMessage?.maxWidth || null;
  }

  @HostBinding('style.--movie-site-landing-gradient-intensity')
  get gradientIntensity() {
    const config = this.config();
    return config.gradientIntensity?.split(',').filter(value => !value.includes('%'))[0] || null;
  }

  @HostBinding('style.--movie-site-landing-gradient-intensity-bottom')
  get gradientIntensityBottom() {
    const config = this.config();
    return config.gradientIntensity?.split(',').filter(value => !value.includes('%'))[1] || null;
  }

  @HostBinding('style.--movie-site-landing-gradient-middle-stop')
  get gradientMiddleStop() {
    const config = this.config();
    return config.gradientIntensity?.split(',').find(value => value.includes('%')) || null;
  }

  videoRef = viewChild<ElementRef<HTMLVideoElement>>('videomedia');

  movie = input.required<Movie>();
  navActions = input.required<NavAction[]>();
  navActionForLanding = computed(() =>
    this.navActions().filter(action => action.type === 'link' && action.fragment !== 'landing')
  );

  config = this.configService.landingConfiguration;
  configVideo = computed(() => this.config() as MovieSiteLandingVideoConfiguration);

  readonly landingEntranceVariant = computed<GsapLandingEntranceVariant | undefined>(() => {
    if (this.config().hideLogo || this.configService.pageConfiguration().disableScrollAnimations) {
      return undefined;
    }

    const variant = getChannelMovieProp('movieSite.gsap.landingEntrance', this.channelService.channel, this.movie());

    return GSAP_LANDING_ENTRANCE_VARIANTS.has(variant ?? '') ? (variant as GsapLandingEntranceVariant) : undefined;
  });

  /**
   * Whether the SSR-safe hiding class should be on the host. Present while the
   * entrance is configured and JS has not yet taken over the visual state via
   * `gsap.set` (which applies matching inline styles). Removing it after the
   * set keeps targets hidden via inline styles, then the reveal tween plays.
   */
  readonly landingEntrancePending = computed(
    () => !!this.landingEntranceVariant() && !this.gsapEntranceStarted()
  );

  constructor() {
    effect(() => {
      // Effect to force video to play when the component is initialized.
      // The video is with autoplay, muted, and playsinline attributes, but
      // sometimes the browser requires an explicit play call.
      const video = this.videoRef()?.nativeElement;
      if (video) {
        video.muted = true; // Ensure video is muted for autoplay
        video.play().catch(error => {
          console.error('Error playing video:', error);
        });
      }
    });

    afterNextRender(() => {
      void this.playLandingEntrance();
    });
  }

  private async playLandingEntrance(): Promise<void> {
    const variant = this.landingEntranceVariant();
    if (!variant || !this.gsapAnimationService.canAnimate()) return;

    const host = this.elementRef.nativeElement as HTMLElement;
    const logo = host.querySelector<HTMLElement>('.movie-site_landing_logo_wrapper movie-site-logo-image');
    const logoImage = logo?.querySelector('img');
    if (!logo || !logoImage) return;

    const releaseMessage = host.querySelector<HTMLElement>('.movie-site_landing_release-message_wrapper');
    const menuItems = Array.from(host.querySelectorAll<HTMLElement>('.movie-site_landing-menu_nav li'));
    const targets = { logo, releaseMessage, menuItems };

    // Apply the matching inline hidden state, then drop the SSR-only CSS hiding
    // class so the reveal tween can take over without a visible-to-hidden snap.
    // SSR HTML is already hidden by the class, so the first paint is hidden.
    await this.gsapAnimationService.setLandingEntranceInitialState(targets, variant);
    this.gsapEntranceStarted.set(true);

    // Wait for the logo image (bounded) so the reveal does not start on a blank frame.
    const defaultView = host.ownerDocument.defaultView;
    if (!logoImage.complete && defaultView) {
      await Promise.race([
        logoImage.decode().catch(() => undefined),
        new Promise<void>(resolve => defaultView.setTimeout(resolve, 1500))
      ]);
    }
    await this.gsapAnimationService.waitForNextAnimationFrame();

    await this.gsapAnimationService.playLandingEntrance(targets, variant);
  }

  get videoBgUrl(): string | null {
    return this.configVideo()?.shortVideoFile || null;
  }
}
